import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Avatar from "../components/Avatar";
import VerifiedBadge from "../components/VerifiedBadge";
import { useAuth } from "../context/AuthContext";
import { Bookmark, Plus, Trash2, X, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

const AVAIL_DOTS = { green: "bg-green-500", yellow: "bg-yellow-400", red: "bg-red-500" };

export default function ShortlistsPage() {
  const { user: me } = useAuth();
  const [lists, setLists] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [newName, setNewName] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/shortlists");
      setLists(data);
      if (data.length > 0 && !selectedId) setSelectedId(data[0].id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load shortlists");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    api
      .get(`/shortlists/${selectedId}`)
      .then(({ data }) => setDetail(data))
      .catch(() => setDetail(null));
  }, [selectedId]);

  const create = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/shortlists", { name });
      setLists((l) => [data, ...l]);
      setSelectedId(data.id);
      setNewName("");
      toast.success(`Shortlist "${name}" created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create shortlist");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/shortlists/${id}`);
      setLists((l) => l.filter((s) => s.id !== id));
      if (selectedId === id) setSelectedId(null);
      toast.success("Shortlist deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete shortlist");
    }
  };

  const removePlayer = async (playerId) => {
    try {
      await api.delete(`/shortlists/${selectedId}/players/${playerId}`);
      setDetail((d) => ({
        ...d,
        players: d.players.filter((p) => p.id !== playerId),
        player_count: d.player_count - 1,
      }));
      setLists((l) =>
        l.map((s) =>
          s.id === selectedId
            ? { ...s, player_ids: s.player_ids.filter((p) => p !== playerId), player_count: s.player_count - 1 }
            : s
        )
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove player");
    }
  };

  if (me && me.role !== "scout" && me.role !== "coach") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10 text-[#a3a3a3]" data-testid="shortlists-denied">
        Shortlists are a scout and coach tool.
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="shortlists-page">
      <h1 className="font-display text-3xl md:text-4xl uppercase tracking-tight text-white mb-1">
        Shortlists
      </h1>
      <p className="text-sm text-[#a3a3a3] mb-6">
        Track the talent you care about across seasons. Add players from Discover or their profile.
      </p>

      <div className="grid md:grid-cols-[280px_1fr] gap-4">
        <div className="space-y-2">
          <form onSubmit={create} className="flex gap-1">
            <input
              data-testid="shortlists-new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New shortlist…"
              maxLength={60}
              className="flex-1 min-w-0 bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-3 py-2 rounded-sm text-sm text-white transition-colors"
            />
            <button
              data-testid="shortlists-create-btn"
              disabled={!newName.trim()}
              className="px-3 py-2 bg-white text-black rounded-sm hover:bg-gray-200 disabled:opacity-50 transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </form>

          {loading ? (
            <div className="text-[#737373] text-sm p-3">Loading…</div>
          ) : lists.length === 0 ? (
            <div data-testid="shortlists-empty" className="bg-[#121212] border border-[#262626] p-4 rounded-sm text-sm text-[#737373]">
              No shortlists yet. Create one above, then add players with the{" "}
              <Bookmark className="w-3.5 h-3.5 inline" /> button on Discover.
            </div>
          ) : (
            lists.map((s) => (
              <div
                key={s.id}
                data-testid={`shortlist-item-${s.id}`}
                className={`flex items-center gap-2 border rounded-sm transition-colors cursor-pointer ${
                  selectedId === s.id
                    ? "bg-[#1a1a1a] border-white"
                    : "bg-[#121212] border-[#262626] hover:border-[#525252]"
                }`}
                onClick={() => setSelectedId(s.id)}
              >
                <div className="flex-1 min-w-0 px-3 py-2.5">
                  <div className="text-sm text-white font-semibold truncate">{s.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#737373]">
                    {s.player_count} player{s.player_count === 1 ? "" : "s"}
                  </div>
                </div>
                <button
                  data-testid={`shortlist-delete-${s.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(s.id);
                  }}
                  className="pr-3 text-[#525252] hover:text-red-400 transition-colors"
                  aria-label={`Delete shortlist ${s.name}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>

        <div>
          {!detail ? (
            <div className="bg-[#121212] border border-[#262626] p-8 rounded-sm text-center text-[#737373]">
              {lists.length === 0 ? "Create a shortlist to get started." : "Select a shortlist."}
            </div>
          ) : detail.players.length === 0 ? (
            <div data-testid="shortlist-detail-empty" className="bg-[#121212] border border-[#262626] p-8 rounded-sm text-center text-[#737373]">
              No players in &ldquo;{detail.name}&rdquo; yet. Use the{" "}
              <Bookmark className="w-3.5 h-3.5 inline" /> button on{" "}
              <Link to="/discover" className="text-white hover:underline">Discover</Link> to add some.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="shortlist-players">
              {detail.players.map((p) => (
                <li
                  key={p.id}
                  data-testid={`shortlist-player-${p.id}`}
                  className="flex items-center gap-3 bg-[#121212] border border-[#262626] p-3 rounded-sm hover:bg-[#1a1a1a] transition-colors"
                >
                  <Link to={`/profile/${p.id}`}>
                    <Avatar url={p.avatar_url} name={p.name} size={44} />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/profile/${p.id}`} className="text-white font-semibold hover:underline inline-flex items-center gap-1.5">
                      {p.name}
                      <VerifiedBadge role={p.role} verified={p.verified} />
                      {AVAIL_DOTS[p.availability] && (
                        <span className={`w-2 h-2 rounded-full shrink-0 ${AVAIL_DOTS[p.availability]}`} />
                      )}
                    </Link>
                    <div className="text-xs text-[#a3a3a3] truncate">
                      {[p.position || "Player", p.team, p.region].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <Link
                    to={`/compare?a=${p.id}`}
                    data-testid={`shortlist-compare-${p.id}`}
                    title="Compare"
                    className="p-2 text-[#737373] hover:text-white transition-colors"
                  >
                    <ArrowLeftRight className="w-4 h-4" />
                  </Link>
                  <button
                    data-testid={`shortlist-remove-${p.id}`}
                    onClick={() => removePlayer(p.id)}
                    title="Remove from shortlist"
                    className="p-2 text-[#737373] hover:text-red-400 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
